import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import ShimmerCard from "./ShimmerCard";
import PerformancesChart from "./PerformanceChart";
import { useNavigate } from "react-router-dom";
import { SquareArrowOutUpRight } from "lucide-react";
const apiBase = process.env.REACT_APP_API_URL;
const playerStatsCache = {};

const PlayerStats = ({ playerId, onClose, player, owned, budget, onBuy, leagueId, isTransferLocked, transferLockThreshold }) => {
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const isBudgetPlayer = transferLockThreshold != null && player?.dynamic_price <= transferLockThreshold;
  const effectiveLocked = isTransferLocked && !isBudgetPlayer;

  useEffect(() => {
    let cancelled = false;

    if (!playerId) return;

    const fetchStats = async () => {
      if (playerStatsCache[playerId]) {
        setGames(playerStatsCache[playerId]);
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${apiBase}/api/player-stats/${playerId}`);
        if (!cancelled) {
          playerStatsCache[playerId] = res.data;
          setGames(res.data || []);
        }
      } catch (err) {
        if (!cancelled) console.error(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [playerId]);

  // Memoize totals, averages, most played champion
  const { totals, averages, mostPlayedChampion } = useMemo(() => {
    const totals = games.reduce(
      (acc, g) => {
        acc.kills += g.kills ?? 0;
        acc.assists += g.assists ?? 0;
        acc.deaths += g.deaths ?? 0;
        acc.cs += g.cs ?? 0;
        acc.points += g.points ?? 0;
        return acc;
      },
      { kills: 0, assists: 0, deaths: 0, cs: 0, points: 0 },
    );

    const averages = games.length
      ? {
          kills: totals.kills / games.length,
          assists: totals.assists / games.length,
          deaths: totals.deaths / games.length,
          cs: totals.cs / games.length,
          points: totals.points / games.length,
        }
      : { kills: 0, assists: 0, deaths: 0, cs: 0, points: 0 };

    const championCounts = games.reduce((acc, g) => {
      if (g.champion_played) acc[g.champion_played] = (acc[g.champion_played] || 0) + 1;
      return acc;
    }, {});

    let mostPlayedChampion = null;
    if (Object.keys(championCounts).length > 0) {
      const sortedChamps = Object.entries(championCounts).sort((a, b) => b[1] - a[1]);
      mostPlayedChampion = { name: sortedChamps[0][0], count: sortedChamps[0][1] };
    }

    return { totals, averages, mostPlayedChampion };
  }, [games]);

  // Memoize sorted games
  const sortedGames = useMemo(() => {
    return [...games].sort((a, b) => new Date(b.games?.start_time).getTime() - new Date(a.games?.start_time).getTime());
  }, [games]);

  const truncate = (num, decimals = 1) => {
    const factor = Math.pow(10, decimals);
    return Math.floor(num * factor) / factor;
  };

  const formatNum = (n) => {
    const num = Number(n);
    if (Number.isNaN(num)) return "0";

    if (num >= 1_000_000) {
      return truncate(num / 1_000_000, 1) + "M";
    }

    if (num >= 1_000) {
      return truncate(num / 1_000, 1) + "K";
    }

    return truncate(num, 1).toFixed(1);
  };

  if (!playerId) return null;

  if (!loading && games.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-matchup">
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              float: "right",
            }}>
            ✕
          </button>

          <div style={{ padding: "40px", color: "white" }}>
            <h2>{player?.name ?? "Player"}</h2>
            <img
              src={`/players/${player.player_image}`}
              style={{
                width: "100%",
                height: "auto",
                objectFit: "contain",
                marginBottom: "10px",
                position: "relative",
              }}
              alt={player.name}
            />
            {/* Buy Section */}
            {player && (
              <div className="player-price-box">
                <p style={{ fontSize: "15px", margin: "0", color: "#ddd", textAlign: "center" }}>
                  Buy <b style={{ color: "rgb(240, 217, 125)" }}>{player.name}</b>
                </p>

                <div style={{ borderRadius: "4px", width: "100%" }}>
                  {player?.status_label === null ? (
                    <button
                      disabled={owned || budget < player.dynamic_price || effectiveLocked}
                      onClick={onBuy}
                      style={{
                        padding: "6px 12px",
                        background: owned || budget < player.dynamic_price || effectiveLocked ? "rgba(236, 233, 233, 0.5)" : "#16b800",
                        border:
                          owned || budget < player.dynamic_price || effectiveLocked ? "1px solid rgba(236, 233, 233, 0.5)" : "1px solid #156f0a",
                        opacity: owned || budget < player.dynamic_price || effectiveLocked ? "0.6" : "1",
                        boxShadow: "rgba(0, 0, 0, 0.4) 0px -5px 0px -2px inset, rgba(236, 233, 233, 0.5) 0px 4px 1px -2px inset",
                        color: "white",
                        fontWeight: "700",
                        borderRadius: "6px",
                        cursor: owned || budget < player.dynamic_price || effectiveLocked ? "not-allowed" : "pointer",
                        width: "100%",
                      }}>
                      {owned ? (
                        "Owned"
                      ) : effectiveLocked ? (
                        "Available after Week 1"
                      ) : budget < player.dynamic_price ? (
                        "Not enough budget"
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
                            <p style={{ margin: "0", fontSize: "14px" }}>${formatNum(player.dynamic_price)}</p>
                          </div>
                        </div>
                      )}
                    </button>
                  ) : (
                    <p style={{ textAlign: "center", opacity: "0.6" }}>Team has been eliminated</p>
                  )}
                </div>
              </div>
            )}
            <p style={{ opacity: 0.7 }}>No games recorded for this player.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-matchup">
          {mostPlayedChampion && (
            <img
              src={`/champion/splash/${mostPlayedChampion.name}_0.jpg`}
              alt={mostPlayedChampion.name}
              loading="lazy"
              className="player-stat-champ-bg"
            />
          )}
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "white",
              fontSize: "16px",
              cursor: "pointer",
              float: "right",
            }}>
            ✕
          </button>

          {loading ? (
            <p>Loading stats...</p>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  width: "96%",
                  marginTop: "40px",
                  position: "relative",
                  zIndex: "10",
                }}>
                <div style={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
                  <div className="player-stat-title">
                    {player?.status_label === null ? (
                      ""
                    ) : (
                      <h4
                        style={{
                          margin: "0",
                          background: "rgb(221, 68, 68)",
                          color: "white",
                          padding: "2px 6px",
                          borderRadius: "90px",
                          fontSize: "14px",
                        }}>
                        {player?.status_label}
                      </h4>
                    )}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: "6px" }}
                      className="player-name-navigate"
                      onClick={() => navigate(`/dashboard/league/${leagueId}/player/${playerId}`)}>
                      <h2>{games[0]?.players?.name ?? "Player"}</h2>
                      <SquareArrowOutUpRight size={18} color="rgb(190, 205, 227)" />
                    </div>
                    <span style={{ display: "flex", alignItems: "center", gap: "2px", marginTop: "-4px" }}>
                      <img
                        src={`/teams/${games[0].players.teams.team_logo}`}
                        alt={games[0].players.teams.name}
                        style={{
                          width: "32px",
                          height: "32px",
                          objectFit: "contain",
                          position: "relative",
                          zIndex: "10",
                        }}
                      />
                      <p style={{ margin: "0", color: "rgb(255 255 255 / 100%)", fontSize: "14px" }}>
                        <b>{games[0]?.players?.teams?.name}</b> | {games[0]?.players?.role}
                      </p>
                    </span>
                    {/* Buy Section */}
                    {player && (
                      <div className="player-price-box">
                        <p style={{ fontSize: "15px", margin: "0", color: "#ddd", textAlign: "center" }}>
                          Buy <b style={{ color: "rgb(240, 217, 125)" }}>{player.name}</b>
                        </p>

                        <div className="shimmer-wrapper" style={{ borderRadius: "4px", width: "100%" }}>
                          {player?.status_label === null ? (
                            <button
                              disabled={owned || budget < player.dynamic_price || effectiveLocked}
                              onClick={onBuy}
                              style={{
                                padding: "6px 12px",
                                background: owned || budget < player.dynamic_price || effectiveLocked ? "rgba(236, 233, 233, 0.5)" : "#16b800",
                                border:
                                  owned || budget < player.dynamic_price || effectiveLocked
                                    ? "1px solid rgba(236, 233, 233, 0.5)"
                                    : "1px solid #156f0a",
                                opacity: owned || budget < player.dynamic_price || effectiveLocked ? "0.6" : "1",
                                boxShadow: "rgba(0, 0, 0, 0.4) 0px -5px 0px -2px inset, rgba(236, 233, 233, 0.5) 0px 4px 1px -2px inset",
                                color: "white",
                                fontWeight: "700",
                                borderRadius: "6px",
                                cursor: owned || budget < player.dynamic_price || effectiveLocked ? "not-allowed" : "pointer",
                                width: "100%",
                              }}>
                              {owned ? (
                                "Owned"
                              ) : effectiveLocked ? (
                                "Available after Week 1"
                              ) : budget < player.dynamic_price ? (
                                "Not enough budget"
                              ) : (
                                <div style={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "2px" }}>
  
                                    <p style={{ margin: "0", fontSize: "14px" }}>${formatNum(player.dynamic_price)}</p>
                                  </div>
                                </div>
                              )}
                            </button>
                          ) : (
                            <p style={{ textAlign: "center", opacity: "0.6" }}>Team has been eliminated</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {games[0]?.players?.player_image && (
                    <ShimmerCard rarity="default">
                      <img
                        src={`/players/${games[0].players.player_image}`}
                        alt={games[0].players.name}
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          marginBottom: "10px",
                          position: "relative",
                        }}
                      />
                    </ShimmerCard>
                  )}
                </div>
              </div>
              <h3 style={{ margin: "0" }} className="player-stat-title">
                Last 20 Games
              </h3>
              <div className="player-stat-modal-numbers">
                <p style={{ color: "rgb(240, 217, 125)" }}>
                  {averages.points.toFixed(1)} <b style={{ fontWeight: "400", fontSize: "12px", opacity: "0.6" }}>Avg. Pts</b>
                </p>
                <p>
                  {totals.kills} <b style={{ fontWeight: "400", fontSize: "12px", opacity: "0.6" }}>Kills</b>
                </p>
                <p>
                  {totals.assists} <b style={{ fontWeight: "400", fontSize: "12px", opacity: "0.6" }}>Assists</b>
                </p>
                <p>
                  {totals.deaths} <b style={{ fontWeight: "400", fontSize: "12px", opacity: "0.6" }}>Deaths</b>
                </p>
                <p>
                  {totals.cs} <b style={{ fontWeight: "400", fontSize: "12px", opacity: "0.6" }}>CS</b>
                </p>
              </div>
              <PerformancesChart playerId={playerId} />
              <div className="fade-out-wrapper">
                <div
                  style={{
                    width: "100%",
                    fontSize: "12px",
                    borderCollapse: "collapse",
                    overflow: "scroll",
                    maxHeight: "260px",
                  }}
                  className="fade-out-down-stat">
                  {sortedGames.map((g, idx, arr) => {
                    const match = g.games?.matches ?? {};
                    const game = g.games ?? {};
                    const opponent = g.opponent_team;
                    const result = g.players?.team_id === game.winner_team_id ? "Win" : "Loss";

                    const prevPoints = arr[idx + 1]?.points ?? null;
                    let trendIcon = null;
                    if (prevPoints !== null) {
                      if (g.points > prevPoints)
                        trendIcon = (
                          <span style={{ color: "#00EE5C", fontSize: "14px", filter: "drop-shadow(0 0 6px rgba(0, 238, 91, 0.4))" }}>▲</span>
                        );
                      else if (g.points < prevPoints)
                        trendIcon = (
                          <span style={{ color: "#DD4444", fontSize: "14px", filter: "drop-shadow(0 0 6px rgba(221, 68, 68, 0.4))" }}>▼</span>
                        );
                      else trendIcon = "-";
                    }

                    return (
                      <div key={g.game_id} className="player-stats-row">
                        <div style={{ minWidth: "27px" }}>
                          <p
                            style={{
                              color: result === "Win" ? "#00EE5C" : "#DD4444",
                              fontWeight: "600",
                              fontSize: "10px",
                              textTransform: "uppercase",
                            }}>
                            {result}
                          </p>
                          <img
                            src={match?.tournaments?.banner_image ? `/banners/${match.tournaments.banner_image}` : ""}
                            style={{ width: "24px", height: "24px", objectFit: "contain" }}
                            alt={match?.tournaments?.name ?? "Tournament"}
                          />
                        </div>
                        <div className="player-avatar" style={{width:"32px", height: "32px"}}>
                          <img
                            src={`/champion/${g.champion_played}.png`}
                            alt={g.champion_played}
                            style={{
                              width: "32px",
                              height: "32px",
                              transform: "scale(1.4)",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        </div>
                        <div>
                          {opponent ? (
                            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                              <p style={{ opacity: "0.6" }}>vs</p>
                              <img
                                src={`/teams/${opponent.team_logo}`}
                                alt={opponent.name}
                                style={{ width: "21px", height: "21px", objectFit: "contain" }}
                              />
                            </div>
                          ) : (
                            "-"
                          )}
                        </div>
                        <h4 style={{ minWidth: "44px" }}>
                          {g.kills ?? 0}/{g.deaths ?? 0}/{g.assists ?? 0}
                        </h4>
                        <h4 style={{ minWidth: "39px" }}>
                          <b style={{ fontWeight: "400", fontSize: "11px", opacity: "0.6" }}>CS</b> {g.cs ?? 0}
                        </h4>
                        <h4 style={{ minWidth: "42px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          {g.points?.toFixed(1) ?? 0}
                          {trendIcon}
                        </h4>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PlayerStats;
